import {useEffect, useState } from "react";
import { useParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { X, Play, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import OrganizationsRepository from "../repositories/OrganizationsRepository";
import ClientInfoRepository from "../repositories/ClientInfoRepository";
import {ClientInfo} from "../models/Client/ClientInfo";
import {DiscoveryCycleQueryParams} from "../repositories/DiscoveryCyclesRepository";
import {MigrationType} from "../models/Statistics/MigrationType";
import {MigrationEventStepType} from "../models/Statistics/MigrationEventStepType";
import DiscoveryCycleTable from "../components/DiscoveryCycles/DiscoveryCycleTable";
import {formatDate} from "../helpers/CommonHelpers";
import { cn } from "@/lib/utils";
import { DateTimeRangePicker, type DateTimeRange } from "@/components/ui/date-time-range-picker";
import { fileFiltersBoxClassName, filterComboboxClassName } from "../components/common/Table/TableStyles";
import { QUERY_KEYS } from "../constants/QueryKeys";
import DocsHelpLink from "@/components/Docs/DocsHelpLink";

export enum DiscoveryCycleFilterRangeType {
  OnlyLastTwoEvents = 'OnlyLastTwoEvents',
  Custom = 'Custom',
}

type CycleTypeId = "cases" | "intakes" | "providers" | "firmDocs" | "templates";

interface CycleTypeDef {
  id: CycleTypeId;
  label: string;
  migrationType: MigrationType;
  queryKey: string;
}

const CYCLE_TYPES: CycleTypeDef[] = [
  { id: "cases", label: "Cases", migrationType: MigrationType.CasesMigration, queryKey: QUERY_KEYS.CASE_CYCLES },
  { id: "intakes", label: "Intakes", migrationType: MigrationType.IntakesMigration, queryKey: QUERY_KEYS.INTAKE_CYCLES },
  { id: "providers", label: "Providers", migrationType: MigrationType.ProvidersMigration, queryKey: QUERY_KEYS.PROVIDER_CYCLES },
  { id: "firmDocs", label: "Firm Documents", migrationType: MigrationType.FirmDocumentsMigration, queryKey: QUERY_KEYS.FIRM_DOCUMENT_CYCLES },
  { id: "templates", label: "Templates", migrationType: MigrationType.TemplatesMigration, queryKey: QUERY_KEYS.TEMPLATE_CYCLES },
];

interface StatusDef {
  id: MigrationEventStepType;
  label: string;
  icon: React.ElementType;
  activeClass: string;
}

const EVENT_STATUSES: StatusDef[] = [
  {
    id: MigrationEventStepType.Started,
    label: "In Progress",
    icon: Play,
    activeClass: "data-[state=on]:bg-amber-100 dark:data-[state=on]:bg-amber-900/40 data-[state=on]:text-amber-800 dark:data-[state=on]:text-amber-300 data-[state=on]:border-amber-300/40",
  },
  {
    id: MigrationEventStepType.Interrupted,
    label: "Interrupted",
    icon: AlertTriangle,
    activeClass: "data-[state=on]:bg-destructive/15 data-[state=on]:text-destructive dark:data-[state=on]:text-red-400 data-[state=on]:border-destructive/30",
  },
  {
    id: MigrationEventStepType.Finished,
    label: "Finished",
    icon: CheckCircle2,
    activeClass: "data-[state=on]:bg-emerald-100 dark:data-[state=on]:bg-emerald-900/40 data-[state=on]:text-emerald-800 dark:data-[state=on]:text-emerald-300 data-[state=on]:border-emerald-300/40",
  },
];

const DiscoveryCyclesPage = () => {
  const organizationsRepository = new OrganizationsRepository();
  const clientInfoRepository = new ClientInfoRepository();
  const queryClient = useQueryClient();
  const { organizationId } = useParams();
  const [clientIds, setClientIds] = useState<string[]>([]);
  const [clientCodeMap, setClientCodeMap] = useState<Record<string, string>>({});

  const [selectedCycleTypes, setSelectedCycleTypes] = useState<CycleTypeId[]>(
    CYCLE_TYPES.map((c) => c.id)
  );
  const [visibleCycleTypes, setVisibleCycleTypes] = useState<CycleTypeId[]>(
    CYCLE_TYPES.map((c) => c.id)
  );

  const [selectedStatuses, setSelectedStatuses] = useState<MigrationEventStepType[]>([
    MigrationEventStepType.Started,
    MigrationEventStepType.Interrupted,
    MigrationEventStepType.Finished,
  ]);

  const [activeTab, setActiveTab] = useState<CycleTypeId>("cases");
  const [tabCounts, setTabCounts] = useState<Partial<Record<CycleTypeId, number>>>({});

  const yesterday = new Date();
  yesterday.setUTCDate(yesterday.getUTCDate() - 1);

  const [queryParams, setQueryParams] = useState<DiscoveryCycleQueryParams>({
    clientIds: [],
    eventSteps: [MigrationEventStepType.Started, MigrationEventStepType.Interrupted, MigrationEventStepType.Finished],
    rangeType: DiscoveryCycleFilterRangeType.OnlyLastTwoEvents,
    fromDate: formatDate(yesterday),
    toDate: '',
    fromTime: '00:00',
    toTime: ''
  });

  const [shouldFetchDiscoveryCycles, setShouldFetchDiscoveryCycles] = useState(true);
  const [selectOpen, setSelectOpen] = useState(false);

  const clientIdsQuery = useQuery({ queryKey: [QUERY_KEYS.CLIENT_IDS, organizationId], queryFn:
    async () => {
      return await organizationsRepository.getOrganizationClientIds(organizationId!);
    }, gcTime: 2 * 60 * 1000,
      refetchInterval: 2 * 60 * 1000,
      refetchOnMount: false, });

  const clientInfosQuery = useQuery<ClientInfo[]>({
    queryKey: [QUERY_KEYS.CLIENT_INFOS_FOR_DISCOVERY, organizationId],
    queryFn: () => clientInfoRepository.getClientInfos(organizationId!),
    gcTime: 5 * 60 * 1000,
    refetchOnMount: false,
    enabled: !!organizationId,
  });

  useEffect(() => {
    if (clientInfosQuery.data) {
      const map: Record<string, string> = {};
      clientInfosQuery.data.forEach((info) => { map[info.id] = info.clientCode; });
      setClientCodeMap(map);
    }
  }, [clientInfosQuery.data]);

  const getClientLabel = (guid: string) => clientCodeMap[guid] || guid;

  useEffect(() => {
    if (clientIdsQuery.data && clientIdsQuery.data !== clientIds) {
      setClientIds(clientIdsQuery.data);
      setQueryParams((prevState) => ({
        ...prevState,
        clientIds: clientIdsQuery.data
      }));
    }
  }, [ clientIdsQuery.data ]);

  // Keep the active tab valid when visible types change
  useEffect(() => {
    if (visibleCycleTypes.length === 0) return;
    if (!visibleCycleTypes.includes(activeTab)) {
      setActiveTab(visibleCycleTypes[0]);
    }
  }, [visibleCycleTypes, activeTab]);

  const handleSelectClientId = (guid: string) => {
    setQueryParams((prevState) => {
      const alreadySelected = prevState.clientIds.includes(guid);
      return {
        ...prevState,
        clientIds: alreadySelected
          ? prevState.clientIds.filter((id) => id !== guid)
          : [...prevState.clientIds, guid]
      };
    });
  };

  const handleUnSelectClientId = (
    guidToUnSelect: string
  ) => {
    setQueryParams((prevState) => ({
      ...prevState,
      clientIds: prevState.clientIds.filter((guid) => guid !== guidToUnSelect)
    }));
  };

  const handleRangeTypeChange = (newValue: DiscoveryCycleFilterRangeType) => {
    setQueryParams((prevParams) => ({
      ...prevParams,
      rangeType: newValue,
    }));
  };

  const handleSubmit = () => {
    if (selectedStatuses.length === 0) {
      toast.error("At least one selected event status (In Progress, Interrupted, or Finished) is required to search");
      return;
    }

    if (selectedCycleTypes.length === 0) {
      toast.error("At least one discovery cycle type is required to search");
      return;
    }

    if (queryParams.clientIds.length === 0) {
      toast.error("At least one selected client is required to search");
      return;
    }

    if (queryParams.rangeType === DiscoveryCycleFilterRangeType.Custom && !queryParams.fromDate) {
      toast.error("Start date is required when the range type is set to Custom");
      return;
    }

    if (queryParams.rangeType === DiscoveryCycleFilterRangeType.Custom && !queryParams.toDate) {
      toast.error("End date is required when the range type is set to Custom");
      return;
    }

    setShouldFetchDiscoveryCycles(true);

    setQueryParams((prev) => ({ ...prev, eventSteps: selectedStatuses }));

    // Invalidate only the selected cycle types' queries
    CYCLE_TYPES.forEach((cycleType) => {
      if (selectedCycleTypes.includes(cycleType.id)) {
        queryClient.invalidateQueries({ queryKey: [cycleType.queryKey] });
      }
    });

    setVisibleCycleTypes(selectedCycleTypes);
    // Drop counts for types we're no longer showing; keep counts for visible
    // types so the badges don't flicker to empty during refetch.
    setTabCounts((prev) => {
      const next: Partial<Record<CycleTypeId, number>> = {};
      for (const id of selectedCycleTypes) {
        if (prev[id] !== undefined) next[id] = prev[id];
      }
      return next;
    });
  };

  const handleResetToDefault = () => {
    const allTypes = CYCLE_TYPES.map((c) => c.id);
    setSelectedCycleTypes(allTypes);
    setVisibleCycleTypes(allTypes);

    const allStatuses = [
      MigrationEventStepType.Started,
      MigrationEventStepType.Interrupted,
      MigrationEventStepType.Finished,
    ];
    setSelectedStatuses(allStatuses);

    handleRangeTypeChange(DiscoveryCycleFilterRangeType.OnlyLastTwoEvents);

    const yesterdayDate = new Date();
    yesterdayDate.setUTCDate(yesterdayDate.getUTCDate() - 1);
    setQueryParams((prevState) => ({
      ...prevState,
      clientIds: clientIds,
      eventSteps: allStatuses,
      fromDate: formatDate(yesterdayDate),
      toDate: '',
      fromTime: '00:00',
      toTime: ''
    }));

    setShouldFetchDiscoveryCycles(true);

    CYCLE_TYPES.forEach((cycleType) => {
      queryClient.invalidateQueries({ queryKey: [cycleType.queryKey] });
    });
  };

  const updateTabCount = (typeId: CycleTypeId, count: number) => {
    setTabCounts((prev) => (prev[typeId] === count ? prev : { ...prev, [typeId]: count }));
  };

  const isCustomRange = queryParams.rangeType === DiscoveryCycleFilterRangeType.Custom;

  const visibleCycleDefs = CYCLE_TYPES.filter((c) => visibleCycleTypes.includes(c.id));

  return (
    <div className="w-full flex-1 min-h-0 overflow-y-auto">
      <div className="flex mt-2 mb-2 gap-2 flex-col sm:flex-row items-start sm:items-center flex-wrap justify-between shrink-0">
        <h2 className="text-3xl font-bold tracking-tight flex items-center gap-2">Discovery Cycles<DocsHelpLink to="concepts/source-links-and-discovery-cycles" /></h2>
        <div className="flex flex-row gap-2">
          <Button size="sm" variant="secondary" onClick={() => handleResetToDefault()}>Reset To Default</Button>
          <Button size="sm" onClick={() => handleSubmit()}>Submit</Button>
        </div>
      </div>

      {/* Row 1: Cycle types, Event status, Range mode */}
      <div className={fileFiltersBoxClassName}>
        <div className={filterComboboxClassName}>
          <div className="flex items-center justify-between">
            <Label className="text-xs font-semibold text-muted-foreground">Cycle Type</Label>
            <button
              type="button"
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              onClick={() =>
                setSelectedCycleTypes(
                  selectedCycleTypes.length === CYCLE_TYPES.length
                    ? []
                    : CYCLE_TYPES.map((c) => c.id)
                )
              }
            >
              {selectedCycleTypes.length === CYCLE_TYPES.length ? "Clear" : "All"}
            </button>
          </div>
          <ToggleGroup
            type="multiple"
            variant="outline"
            size="sm"
            value={selectedCycleTypes}
            onValueChange={(v) => setSelectedCycleTypes(v as CycleTypeId[])}
          >
            {CYCLE_TYPES.map((ct) => (
              <ToggleGroupItem
                key={ct.id}
                value={ct.id}
                aria-label={ct.label}
              >
                {ct.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
        <div className={filterComboboxClassName}>
          <div className="flex items-center justify-between">
            <Label className="text-xs font-semibold text-muted-foreground">Event Status</Label>
            <button
              type="button"
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              onClick={() =>
                setSelectedStatuses(
                  selectedStatuses.length === EVENT_STATUSES.length
                    ? []
                    : EVENT_STATUSES.map((s) => s.id)
                )
              }
            >
              {selectedStatuses.length === EVENT_STATUSES.length ? "Clear" : "All"}
            </button>
          </div>
          <ToggleGroup
            type="multiple"
            variant="outline"
            size="sm"
            value={selectedStatuses}
            onValueChange={(v) => setSelectedStatuses(v as MigrationEventStepType[])}
          >
            {EVENT_STATUSES.map((s) => {
              const Icon = s.icon;
              return (
                <ToggleGroupItem
                  key={s.id}
                  value={s.id}
                  aria-label={s.label}
                  className={cn("gap-1.5", s.activeClass)}
                >
                  <Icon className="size-3.5" />
                  {s.label}
                </ToggleGroupItem>
              );
            })}
          </ToggleGroup>
        </div>
      </div>

      {/* Row 2: Range mode toggle, Date range picker, Clients */}
      <div className={fileFiltersBoxClassName}>
        <div className="flex-1 min-w-[180px] [&>label]:mb-0.5">
          <Label className="text-xs font-semibold text-muted-foreground">Range</Label>
          <ToggleGroup
            type="single"
            value={queryParams.rangeType}
            onValueChange={(value) => { if (value) handleRangeTypeChange(value as DiscoveryCycleFilterRangeType); }}
            variant="outline"
            size="sm"
            className="w-full my-1"
          >
            <ToggleGroupItem value={DiscoveryCycleFilterRangeType.OnlyLastTwoEvents} className="flex-1">
              Last 2 Events
            </ToggleGroupItem>
            <ToggleGroupItem value={DiscoveryCycleFilterRangeType.Custom} className="flex-1">
              Custom
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
        <div className={cn("flex-1 min-w-[320px] [&>label]:mb-0.5", !isCustomRange && "opacity-50 pointer-events-none")}>
          <Label className="text-xs font-semibold text-muted-foreground">Date Range</Label>
          <DateTimeRangePicker
            className="w-full"
            value={{
              fromDate: queryParams.fromDate ?? "",
              fromTime: queryParams.fromTime ?? "",
              toDate: queryParams.toDate ?? "",
              toTime: queryParams.toTime ?? "",
            }}
            onChange={(range: DateTimeRange) =>
              setQueryParams((prev) => ({
                ...prev,
                fromDate: range.fromDate,
                fromTime: range.fromTime,
                toDate: range.toDate,
                toTime: range.toTime,
              }))
            }
          />
        </div>
        <div className={filterComboboxClassName}>
          <Label className="text-xs font-semibold text-muted-foreground">Clients</Label>
          <div className="relative">
            <button
              type="button"
              className="w-full min-h-9 py-1.5 px-3 text-sm border rounded-md bg-background text-left"
              onClick={() => setSelectOpen(!selectOpen)}
            >
              {queryParams.clientIds.length === 0 ? (
                <span className="text-muted-foreground">Select clients</span>
              ) : (
                <div className="flex gap-1 flex-wrap max-h-[68px] overflow-y-auto">
                  {queryParams.clientIds.map((guid) => (
                    <span
                      key={guid}
                      className="inline-flex items-center rounded-full bg-primary/10 text-primary px-2 py-0.5 text-xs font-semibold"
                      title={guid}
                    >
                      {getClientLabel(guid)}
                      <button
                        className="ml-1 hover:text-destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUnSelectClientId(guid);
                        }}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </button>
            {selectOpen && (
              <div className="absolute z-50 mt-1 w-full border rounded-md bg-background shadow-lg max-h-[200px] overflow-y-auto">
                {clientIds.map((guid) => (
                  <button
                    key={guid}
                    className={cn(
                      "w-full text-left px-3 py-2 text-sm hover:bg-muted",
                      queryParams.clientIds.includes(guid) && "bg-primary/10 text-primary"
                    )}
                    onClick={() => handleSelectClientId(guid)}
                    title={guid}
                  >
                    {getClientLabel(guid)}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Results */}
      <Card className="mt-2">
        <CardContent>
          {clientIds.length === 0 && (
            <p className="text-sm text-muted-foreground py-4 text-center">No clients found for this organization. Discovery cycles will appear here once clients are registered.</p>
          )}

          {clientIds.length !== 0 && visibleCycleDefs.length === 0 && (
            <p className="text-sm text-muted-foreground py-4 text-center">No cycle types selected. Pick at least one type in the filters and submit.</p>
          )}

          {clientIds.length !== 0 && visibleCycleDefs.length > 0 && (
            <>
              <ToggleGroup
                type="single"
                value={activeTab}
                onValueChange={(v) => { if (v) setActiveTab(v as CycleTypeId); }}
                variant="outline"
                size="sm"
                className="w-full my-1 flex-wrap h-auto"
              >
                {visibleCycleDefs.map((ct) => {
                  const count = tabCounts[ct.id];
                  return (
                    <ToggleGroupItem key={ct.id} value={ct.id} className="flex-1 gap-1.5">
                      {ct.label}
                      {count !== undefined && (
                        <Badge variant="neutral" className="px-1.5 py-0 text-[10px] leading-4">
                          {count}
                        </Badge>
                      )}
                    </ToggleGroupItem>
                  );
                })}
              </ToggleGroup>

              {visibleCycleDefs.map((ct) => (
                <div
                  key={ct.id}
                  className={cn("mt-4", activeTab !== ct.id && "hidden")}
                >
                  <DiscoveryCycleTable
                    queryKey={ct.queryKey}
                    migrationType={ct.migrationType}
                    organizationId={organizationId ?? ""}
                    queryParams={queryParams}
                    shouldFetchDiscoveryCycles={shouldFetchDiscoveryCycles}
                    setShouldFetchDiscoveryCycles={setShouldFetchDiscoveryCycles}
                    onCountChange={(count) => updateTabCount(ct.id, count)}
                  />
                </div>
              ))}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
};

export default DiscoveryCyclesPage;
